import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './PropertyListing.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import NewsletterSubscribe from './NewsletterSubscribe';

interface BookingFormData {
  checkIn: Date | null;
  checkOut: Date | null;
}

interface PricingData {
  price: number;
  bookingStatus: string;
  unbookable: number;
  minStay: number;
}

const PropertyListing: React.FC = () => {
  const navigate = useNavigate();
  const S3_BASE_URL = 'https://directbooking-images-071128453175.s3.us-west-2.amazonaws.com';
  const propertyImages = [
    `${S3_BASE_URL}/6.jpg`,
    `${S3_BASE_URL}/1.jpg`,
    `${S3_BASE_URL}/2.jpg`,
    `${S3_BASE_URL}/3.jpg`,
    `${S3_BASE_URL}/4.jpg`,
    `${S3_BASE_URL}/5.jpg`,
    `${S3_BASE_URL}/7.jpg`,
    `${S3_BASE_URL}/8.jpg`,
    `${S3_BASE_URL}/9.jpg`,
    `${S3_BASE_URL}/10.jpg`,
    `${S3_BASE_URL}/11.jpg`,
    `${S3_BASE_URL}/12.jpg`,
    `${S3_BASE_URL}/13.jpg`,
    `${S3_BASE_URL}/14.jpg`,
    `${S3_BASE_URL}/15.jpg`,
    `${S3_BASE_URL}/16.jpg`,
    `${S3_BASE_URL}/17.jpg`,
    `${S3_BASE_URL}/18.jpg`,
    `${S3_BASE_URL}/19.jpg`,
    `${S3_BASE_URL}/20.jpg`,
    `${S3_BASE_URL}/21.jpg`,
    `${S3_BASE_URL}/22.jpg`,
    `${S3_BASE_URL}/23.jpg`,
    `${S3_BASE_URL}/24.jpg`,
    `${S3_BASE_URL}/25.jpg`,
    `${S3_BASE_URL}/26.jpg`,
    `${S3_BASE_URL}/27.jpg`,
    `${S3_BASE_URL}/28.jpg`,
    `${S3_BASE_URL}/29.jpg`,
    `${S3_BASE_URL}/30.jpg`,
    `${S3_BASE_URL}/31.jpg`,
    `${S3_BASE_URL}/32.jpg`,
    `${S3_BASE_URL}/33.jpg`,
    `${S3_BASE_URL}/34.jpg`,
    `${S3_BASE_URL}/35.jpg`,
    `${S3_BASE_URL}/36.jpg`,
    `${S3_BASE_URL}/37.jpg`,
    `${S3_BASE_URL}/38.jpg`,
    `${S3_BASE_URL}/39.jpg`,
    `${S3_BASE_URL}/40.jpg`,
    `${S3_BASE_URL}/41.jpg`,
  ];

  const [formData, setFormData] = useState<BookingFormData>({
    checkIn: null,
    checkOut: null
  });

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [pricing, setPricing] = useState<{[date: string]: PricingData}>({});
  const [isLoadingPricing, setIsLoadingPricing] = useState(false);
  const [pricingApiError, setPricingApiError] = useState<string>('');
  const [calculatedPricing, setCalculatedPricing] = useState<{
    nights: number;
    subtotal: number;
    discountPercent: number;
    discountReason: string;
    discountAmount: number;
    taxPercent: number;
    taxAmount: number;
    total: number;
  } | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [pricingError, setPricingError] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate dates are available
    if (!isDateRangeAvailable(formData.checkIn, formData.checkOut)) {
      alert('Sorry, the selected dates are not available. Please choose different dates.');
      return;
    }

    // Validate we have calculated pricing
    if (!calculatedPricing || !formData.checkIn || !formData.checkOut) {
      alert('Unable to calculate pricing. Please try again.');
      return;
    }

    // Navigate to house rules with booking data
    navigate('/house-rules', {
      state: {
        bookingData: {
          checkIn: formData.checkIn,
          checkOut: formData.checkOut,
          nights: calculatedPricing.nights,
          subtotal: calculatedPricing.subtotal,
          discountAmount: calculatedPricing.discountAmount,
          taxAmount: calculatedPricing.taxAmount,
          total: calculatedPricing.total
        }
      }
    });
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setFormData({
      checkIn: start,
      checkOut: end
    });
  };

  const openGallery = (index: number) => {
    setCurrentImageIndex(index);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % propertyImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + propertyImages.length) % propertyImages.length);
  };

  const fetchPricing = async () => {
    setIsLoadingPricing(true);
    setPricingApiError('');
    try {
      // Use API Gateway Lambda proxy to avoid CORS issues
      const response = await fetch('https://api.theacademicloft.com/pricing');

      const data = await response.json();
      console.log('PriceLabs API Response:', data);

      if (!response.ok) {
        // API returned an error
        setPricingApiError('Unable to load pricing and availability at this time. Please try again later or contact us directly.');
        return;
      }

      // Parse pricing and availability data into a date -> PricingData map
      // PriceLabs returns an array of listings, each with a data array
      if (data && Array.isArray(data) && data.length > 0) {
        const listing = data[0];
        const priceMap: {[date: string]: PricingData} = {};

        if (listing.data && Array.isArray(listing.data)) {
          listing.data.forEach((item: any) => {
            if (item.date && item.price) {
              priceMap[item.date] = {
                price: item.price,
                bookingStatus: item.booking_status || '',
                unbookable: item.unbookable || 0,
                minStay: item.min_stay || 1
              };
            }
          });
        }

        console.log('Parsed pricing map:', priceMap);
        console.log('Sample dates from pricing:', Object.keys(priceMap).slice(0, 5));
        setPricing(priceMap);
        setPricingApiError('');
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      setPricingApiError('Unable to load pricing and availability at this time. Please try again later or contact us directly.');
    } finally {
      setIsLoadingPricing(false);
    }
  };

  // Load pricing on component mount
  useEffect(() => {
    fetchPricing();
  }, []);

  // Check if a date is blocked based on PriceLabs booking status
  const isDateBlocked = (date: Date): boolean => {
    const dateStr = date.toISOString().split('T')[0];
    const dateData = pricing[dateStr];

    if (!dateData) return false; // If no data, assume available

    // Block if date is booked or unbookable
    return dateData.unbookable === 1 ||
           dateData.bookingStatus.toLowerCase().includes('booked') ||
           dateData.bookingStatus.toLowerCase().includes('unavailable');
  };

  // Get minimum checkout date based on check-in date's min_stay requirement
  const getMinCheckoutDate = (checkIn: Date | null): Date | null => {
    if (!checkIn) return null;

    const checkInStr = checkIn.toISOString().split('T')[0];
    const checkInData = pricing[checkInStr];

    if (!checkInData) {
      // Default to 1 night minimum if no data
      return new Date(checkIn.getTime() + 86400000);
    }

    // Add minStay nights to check-in date
    return new Date(checkIn.getTime() + (checkInData.minStay * 86400000));
  };

  // Check if date range is available and meets min_stay requirements
  const isDateRangeAvailable = (checkIn: Date | null, checkOut: Date | null): boolean => {
    if (!checkIn || !checkOut) return true;

    const start = new Date(checkIn);
    const end = new Date(checkOut);

    // Calculate number of nights
    const nights = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    // Check min_stay requirement for check-in date
    const checkInStr = start.toISOString().split('T')[0];
    const checkInData = pricing[checkInStr];

    if (checkInData && checkInData.minStay > nights) {
      return false; // Stay is shorter than minimum required
    }

    // Check all dates in range are available
    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
      if (isDateBlocked(d)) {
        return false;
      }
    }

    return true;
  };

  // Calculate pricing with discounts (server-side validation)
  const calculatePricing = async (checkIn: Date, checkOut: Date) => {
    setIsCalculating(true);
    setPricingError('');
    try {
      const response = await fetch('https://api.theacademicloft.com/calculate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          checkIn: checkIn.toISOString().split('T')[0],
          checkOut: checkOut.toISOString().split('T')[0]
        })
      });

      const data = await response.json();
      console.log('Calculated pricing:', data);

      if (response.ok) {
        setCalculatedPricing(data);
        setPricingError('');
      } else {
        console.error('Pricing calculation error:', data);
        setCalculatedPricing(null);
        setPricingError('Unable to calculate pricing at this time. Please try again later or contact us directly.');
      }
    } catch (error) {
      console.error('Error calculating pricing:', error);
      setCalculatedPricing(null);
      setPricingError('Unable to calculate pricing at this time. Please try again later or contact us directly.');
    } finally {
      setIsCalculating(false);
    }
  };

  // Recalculate pricing when dates change
  useEffect(() => {
    if (formData.checkIn && formData.checkOut && isDateRangeAvailable(formData.checkIn, formData.checkOut)) {
      calculatePricing(formData.checkIn, formData.checkOut);
    } else {
      setCalculatedPricing(null);
      setPricingError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.checkIn, formData.checkOut]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isGalleryOpen) return;

      if (e.key === 'Escape') {
        closeGallery();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGalleryOpen]);

  return (
    <div className="property-container">
      <header className="property-header">
        <h1>Academic Loft - Modern Home Near UCR</h1>
        <p className="location">📍 Riverside, California</p>
        <p className="rating">⭐ 5.0 · 6 reviews</p>
      </header>

      <section className="image-gallery">
        <div className="main-image" onClick={() => openGallery(0)}>
          <img src={propertyImages[0]} alt="Academic Loft main view" />
        </div>
        <div className="thumbnail-grid">
          {propertyImages.slice(1, 5).map((image, index) => (
            <div key={index} className="thumbnail" onClick={() => openGallery(index + 1)}>
              <img src={image} alt={`Property view ${index + 2}`} />
            </div>
          ))}
        </div>
        <button className="view-all-photos-btn" onClick={() => openGallery(0)}>
          📷 View all {propertyImages.length} photos
        </button>
      </section>

      <div className="content-grid">
        <main className="property-details">
          <section className="property-info">
            <h2>Newly Renovated Home - Perfect for UCR Visitors</h2>
            <div className="specs">
              <span>👥 Up to 5 guests</span>
              <span>🛏️ 3 bedrooms</span>
              <span>🚿 2 bathrooms</span>
            </div>
          </section>

          <section className="description">
            <h3>About This Property</h3>
            <p>
              Welcome to our beautifully renovated home located just minutes from UC Riverside campus.
              This modern open-floor-plan house offers comfortable accommodations perfect for academic visitors,
              families, or anyone seeking a peaceful retreat in Riverside.
            </p>
            <p>
              Professionally cleaned before each stay and situated in a secure gated community, this home
              provides all the amenities you need for a comfortable extended stay or short visit.
            </p>
          </section>

          <section className="bedrooms">
            <h3>Sleeping Arrangements</h3>
            <div className="bedroom-list">
              <div className="bedroom">
                <h4>🛏️ Primary Bedroom</h4>
                <p>Queen bed</p>
              </div>
              <div className="bedroom">
                <h4>🛏️ Bedroom 2</h4>
                <p>Queen bed + Work desk</p>
              </div>
              <div className="bedroom">
                <h4>🛏️ Bedroom 3</h4>
                <p>Full bed + Work desk</p>
              </div>
            </div>
          </section>

          <section className="amenities">
            <h3>Amenities & Features</h3>
            <div className="amenities-grid">
              <div className="amenity-category">
                <h4>Kitchen & Dining</h4>
                <ul>
                  <li>🍳 Fully stocked kitchen</li>
                  <li>🍳 New appliances</li>
                  <li>🧺 Dishwasher</li>
                  <li>☕ Coffee station</li>
                </ul>
              </div>
              <div className="amenity-category">
                <h4>Comfort & Entertainment</h4>
                <ul>
                  <li>📺 Roku Smart TV</li>
                  <li>📶 Fast Wi-Fi</li>
                  <li>🧺 Washer & dryer</li>
                  <li>❄️ Climate control</li>
                </ul>
              </div>
              <div className="amenity-category">
                <h4>Parking & Security</h4>
                <ul>
                  <li>🚗 2-car garage</li>
                  <li>🔒 Gated community</li>
                  <li>🔒 Secure entry</li>
                </ul>
              </div>
              <div className="amenity-category">
                <h4>Bathroom Essentials</h4>
                <ul>
                  <li>🧼 Shampoo & conditioner</li>
                  <li>🧼 Body wash & soap</li>
                  <li>🛁 Tub/shower combo</li>
                  <li>🚿 Walk-in shower</li>
                </ul>
              </div>
              <div className="amenity-category">
                <h4>Property Features</h4>
                <ul>
                  <li>🏡 Newly renovated</li>
                  <li>🏡 Open-floor-plan</li>
                  <li>🧽 Professionally cleaned</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="location-info">
            <h3>Location Highlights</h3>
            <ul>
              <li>📚 4 minutes to UC Riverside campus</li>
              <li>🏘️ Quiet gated community</li>
              <li>🛒 Close to shopping and dining</li>
              <li>🚗 Easy freeway access</li>
            </ul>

            <h3 style={{ marginTop: '30px' }}>Where You'll Be</h3>
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3306.1234567890123!2d-117.344111!3d33.989944!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDU5JzIzLjgiTiAxMTfCsDIwJzM4LjgiVw!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: '8px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Property Location"
              ></iframe>
            </div>
          </section>
        </main>

        <aside className="booking-sidebar">
          <div className="booking-card">
            <h3>Book Your Stay</h3>
            <p className="direct-booking-note">
              📞 Book direct and save! No booking fees.
            </p>
            <form onSubmit={handleSubmit} className="booking-form">
              {pricingApiError ? (
                <div className="pricing-error-full">
                  <p className="error-message">⚠️ {pricingApiError}</p>
                </div>
              ) : (
                <>
                  <div className="form-group date-range-group">
                    <label>Select Dates *</label>
                    <DatePicker
                      selectsRange={true}
                      startDate={formData.checkIn}
                      endDate={formData.checkOut}
                      onChange={handleDateChange}
                      minDate={new Date()}
                      filterDate={(date) => !isDateBlocked(date)}
                      dayClassName={(date) => isDateBlocked(date) ? 'blocked-date' : 'available-date'}
                      placeholderText="Select check-in and check-out dates"
                      dateFormat="MM/dd/yyyy"
                      disabled={isLoadingPricing}
                      required
                      monthsShown={2}
                      className="date-picker-input date-range-picker"
                    />
                  </div>

                  {isLoadingPricing && (
                    <p className="calendar-loading">Loading availability and pricing...</p>
                  )}
                </>
              )}

              {!pricingApiError && (
                <>
                  {!isLoadingPricing && formData.checkIn && !formData.checkOut && (
                    <div className="min-stay-info">
                      {(() => {
                        const checkInStr = formData.checkIn.toISOString().split('T')[0];
                        const checkInData = pricing[checkInStr];
                        if (checkInData && checkInData.minStay > 1) {
                          return `ℹ️ Minimum stay: ${checkInData.minStay} nights for this check-in date`;
                        }
                        return null;
                      })()}
                    </div>
                  )}

                  {!isLoadingPricing && formData.checkIn && formData.checkOut && (
                    <div className={`availability-message ${isDateRangeAvailable(formData.checkIn, formData.checkOut) ? 'available' : 'unavailable'}`}>
                      {(() => {
                        if (isDateRangeAvailable(formData.checkIn, formData.checkOut)) {
                          return '✓ These dates are available!';
                        }

                        // Check if it's a min_stay issue
                        const checkInStr = formData.checkIn.toISOString().split('T')[0];
                        const checkInData = pricing[checkInStr];
                        const nights = Math.ceil((formData.checkOut.getTime() - formData.checkIn.getTime()) / (1000 * 60 * 60 * 24));

                        if (checkInData && checkInData.minStay > nights) {
                          return `✗ Minimum stay of ${checkInData.minStay} night${checkInData.minStay > 1 ? 's' : ''} required for this check-in date.`;
                        }

                        return '✗ These dates are not available. Please select different dates.';
                      })()}
                    </div>
                  )}

                  {formData.checkIn && formData.checkOut && isDateRangeAvailable(formData.checkIn, formData.checkOut) && (
                    <div className="pricing-breakdown">
                      <h4>Price Breakdown</h4>
                      {isCalculating ? (
                        <p className="pricing-loading">Calculating prices...</p>
                      ) : pricingError ? (
                        <div className="pricing-error">
                          <p className="error-message">⚠️ {pricingError}</p>
                        </div>
                      ) : calculatedPricing ? (
                        <>
                          <div className="pricing-row">
                            <span>${(calculatedPricing.subtotal / calculatedPricing.nights).toFixed(2)} x {calculatedPricing.nights} night{calculatedPricing.nights > 1 ? 's' : ''}</span>
                            <span>${calculatedPricing.subtotal.toFixed(2)}</span>
                          </div>
                          {calculatedPricing.discountPercent > 0 && (
                            <div className="pricing-row discount-row">
                              <span>{calculatedPricing.discountReason} ({calculatedPricing.discountPercent}% off)</span>
                              <span className="discount-amount">-${calculatedPricing.discountAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="pricing-row tax-row">
                            <span>Transient Occupancy Tax ({calculatedPricing.taxPercent}%)</span>
                            <span>${calculatedPricing.taxAmount.toFixed(2)}</span>
                          </div>
                          <div className="pricing-divider"></div>
                          <div className="pricing-row pricing-total">
                            <span>Total</span>
                            <span>${calculatedPricing.total.toFixed(2)}</span>
                          </div>
                        </>
                      ) : (
                        <p className="pricing-loading">Unable to calculate pricing</p>
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={!calculatedPricing || isCalculating || !!pricingError}
                  >
                    Reserve now
                  </button>

                  <p className="contact-info-note">
                    <Link to="/contact" className="contact-us-link">Contact us</Link> to discuss custom pricing options or monthly stays.
                  </p>
                </>
              )}
            </form>
          </div>
        </aside>
      </div>

      {isGalleryOpen && (
        <div className="gallery-modal" onClick={closeGallery}>
          <button className="close-modal" onClick={closeGallery}>×</button>
          <button className="nav-button prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}>
            ‹
          </button>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={propertyImages[currentImageIndex]}
              alt={`Property view ${currentImageIndex + 1}`}
            />
            <div className="image-counter">
              {currentImageIndex + 1} / {propertyImages.length}
            </div>
          </div>
          <button className="nav-button next" onClick={(e) => { e.stopPropagation(); nextImage(); }}>
            ›
          </button>
        </div>
      )}

      <footer className="page-footer">
        <div className="footer-newsletter">
          <h3>Stay in the Loop</h3>
          <p>Get exclusive offers, updates, and booking tips delivered to your inbox.</p>
          <NewsletterSubscribe inline={true} />
        </div>
        <div className="footer-bottom">
          <p>© 2024 Academic Loft. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/terms">Terms & Conditions</Link>
            <span>•</span>
            <Link to="/house-rules">House Rules</Link>
            <span>•</span>
            <Link to="/contact">Contact Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PropertyListing;
